import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import privateAxios from "../utils/axiosPrivate";
import {
  getActivationURL,
  getRoleManagementURL,
  getUserPermissionUrl,
  getUserRoleListURL,
  limitedAccessModelURL
} from "../utils/constants/apiEndPoints";
import { showToast } from "../utils/utils";
import { showToastType } from "../utils/constants/appConstants";
import { commonErrMsg } from "../locales/en";

// Initial state
const initialState = {
  loading: false,
  userListLoading: false,
  rolesData: [],
  error: null,
  permissions: [],
  isfullAccess: false,
  permissionsLoading: false,
  permissionsFetched: false
};

// Slice
const roleManagementSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    setRoles(state, action) {
      state.rolesData = action.payload;
    },
    setRolesLoading(state, action) {
      state.loading = action.payload;
    },
    setUserListLoading(state, action) {
      state.userListLoading = action.payload;
    },
    setRolesError(state, action) {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserRoleListThunk.pending, (state) => {
        state.userListLoading = true;
      })
      .addCase(getUserRoleListThunk.fulfilled, (state) => {
        state.userListLoading = false;
      })

      .addCase(getUserPermission.pending, (state) => {
        state.permissionsLoading = true;
        state.permissionsFetched = false;
      })

      .addCase(getUserPermission.fulfilled, (state, action) => {
        state.permissionsLoading = false;
        state.permissionsFetched = true;

        state.permissions = action.payload?.data?.data?.permissions || [];

        state.isfullAccess = action.payload?.data?.data?.isfullAccess ?? false;
      })

      .addCase(getUserPermission.rejected, (state) => {
        state.permissionsLoading = false;
        state.permissionsFetched = true;
        state.permissions = [];
        state.isfullAccess = false;
      });
  }
});

// Thunk: get roles
export const getRolesThunk = createAsyncThunk(
  "roles/getRolesThunk",
  async (payload, { dispatch }) => {
    try {
      dispatch(setRolesLoading(true));

      const response = await privateAxios.get(getRoleManagementURL, {
        params: payload
      });

      return response;
    } finally {
      dispatch(setRolesLoading(false));
    }
  }
);

// Thunk: get User role List
export const getUserRoleListThunk = createAsyncThunk(
  "roles/getUserRoleListThunk",
  async (payload) => {
    try {
      const response = await privateAxios.get(getUserRoleListURL(payload.id), {
        params: payload.params
      });

      return response;
    } catch {
      // handle catch here;
    }
  }
);

// Thunk: activate role
export const activateRoleThunk = createAsyncThunk(
  "roles/activateRoleThunk",
  async (payload, { dispatch }) => {
    try {
      const response = await privateAxios.patch(getActivationURL(payload));

      return response;
    } catch (error) {
      if (error.response.status === 400) {
        showToast(error?.response?.data?.message, showToastType.error);
      } else {
        const message =
          error?.response?.data?.errors?.[0] ||
          error?.response?.data?.message ||
          error.message ||
          commonErrMsg.item7;

        showToast(message, showToastType.error);
      }
    } finally {
      dispatch(setRolesLoading(false));
    }
  }
);

// Thunk: activate role
export const limitedAccessModelThunk = createAsyncThunk(
  "roles/limitedAccessModel",
  async (payload, { dispatch }) => {
    try {
      const response = await privateAxios.get(
        limitedAccessModelURL(Number(payload))
      );

      return response;
    } catch (error) {
      const message =
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        error.message ||
        commonErrMsg.item7;

      showToast(message, showToastType.error);
    } finally {
      dispatch(setRolesLoading(false));
    }
  }
);

export const getUserPermission = createAsyncThunk(
  "auth/getUserPermission",
  async (_, { rejectWithValue }) => {
    try {
      const response = await privateAxios.post(getUserPermissionUrl);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Actions & Reducer
export const { setRoles, setRolesLoading, setRolesError, setUserListLoading } =
  roleManagementSlice.actions;

export default roleManagementSlice.reducer;
